import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import {
  createCart,
  fetchCart,
  addLine,
  updateLine,
  removeLine,
} from "@/lib/shopify";

const CartContext = createContext(null);
const STORAGE_KEY = "gt_cart_id";

export function CartProvider({ children }) {
  const [cart, setCart] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // Restore existing cart on load
  useEffect(() => {
    const id = localStorage.getItem(STORAGE_KEY);
    if (!id) return;
    fetchCart(id)
      .then((c) => {
        if (c && c.lines) setCart(c);
        else localStorage.removeItem(STORAGE_KEY);
      })
      .catch(() => localStorage.removeItem(STORAGE_KEY));
  }, []);

  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);

  const addItem = useCallback(
    async (variantId, quantity = 1) => {
      setBusy(true);
      try {
        let next;
        const existingId = cart?.id || localStorage.getItem(STORAGE_KEY);
        if (existingId) {
          next = await addLine(existingId, variantId, quantity);
        } else {
          next = await createCart(variantId, quantity);
        }
        if (next?.id) localStorage.setItem(STORAGE_KEY, next.id);
        setCart(next);
        setIsOpen(true);
        return next;
      } finally {
        setBusy(false);
      }
    },
    [cart]
  );

  const setLineQuantity = useCallback(
    async (lineId, quantity) => {
      if (!cart?.id) return;
      setBusy(true);
      try {
        const next =
          quantity <= 0
            ? await removeLine(cart.id, lineId)
            : await updateLine(cart.id, lineId, quantity);
        setCart(next);
      } finally {
        setBusy(false);
      }
    },
    [cart]
  );

  const removeItem = useCallback(
    async (lineId) => {
      if (!cart?.id) return;
      setBusy(true);
      try {
        const next = await removeLine(cart.id, lineId);
        setCart(next);
      } finally {
        setBusy(false);
      }
    },
    [cart]
  );

  const checkout = useCallback(() => {
    if (cart?.checkoutUrl) window.location.href = cart.checkoutUrl;
  }, [cart]);

  const count = cart?.totalQuantity || 0;

  const value = {
    cart,
    count,
    busy,
    isOpen,
    openCart,
    closeCart,
    addItem,
    setLineQuantity,
    removeItem,
    checkout,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
